import type { SchemaOrgNode } from './schema-org-node.model';

const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Reserved Schema.org keys that CMS-authored `schemaAdditions` must never
 * override on the node they merge into: `@id`/`@type` define the node's
 * identity and type (changing them breaks dedupe + `@id` cross-references and
 * could shadow other nodes in the consolidated `@graph`), and `@context` is
 * emitted once at the graph root. Stripped from the top level of the additions
 * only — nested objects may still carry their own `@type` (legitimate enrichment,
 * e.g. adding an `aggregateRating`/`hasCourseInstance` sub-node).
 */
const RESERVED_IDENTITY_KEYS = ['@id', '@type', '@context'];

/**
 * Recursively deep-merges source into target.
 * Arrays and primitives from source overwrite target values.
 * Nested objects are merged recursively.
 */
export function deepMergeSchema(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
): Record<string, unknown> {
  const result = { ...target };
  for (const key of Object.keys(source)) {
    if (UNSAFE_KEYS.has(key)) continue;
    const targetVal = target[key];
    const sourceVal = source[key];
    if (isPlainObject(targetVal) && isPlainObject(sourceVal)) {
      result[key] = deepMergeSchema(targetVal, sourceVal);
    } else {
      result[key] = sourceVal;
    }
  }
  return result;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Deep-merges `override` into `base` with `override` values winning per-key, while
 * protecting the base node's identity: `@id`/`@type`/`@context` are never taken
 * from `override` (see `RESERVED_IDENTITY_KEYS`). Component-only keys on `base` are
 * retained. Shared by the Product/Course `schemaAdditions` path (EPS-3686) and the
 * CMS-vs-component precedence merge (EPS-4243) so both honor the same identity
 * rules. Returns the reserved keys that were stripped, so callers can warn.
 */
export function deepMergeSchemaNodes(
  base: SchemaOrgNode,
  override: Record<string, unknown>,
): { merged: SchemaOrgNode; strippedReserved: string[] } {
  const safeOverride: Record<string, unknown> = { ...override };
  const strippedReserved: string[] = [];
  for (const key of RESERVED_IDENTITY_KEYS) {
    if (key in safeOverride) {
      delete safeOverride[key];
      strippedReserved.push(key);
    }
  }
  return { merged: deepMergeSchema(base, safeOverride) as SchemaOrgNode, strippedReserved };
}

/**
 * Parses a schemaAdditions JSON string and deep-merges it into a schema node.
 * Returns the original schema unchanged if the field is empty, null, or contains invalid JSON.
 */
export function applySchemaAdditions(schema: SchemaOrgNode, schemaAdditions: string | undefined | null): SchemaOrgNode {
  if (!schemaAdditions) {
    return schema;
  }

  try {
    const overrides = JSON.parse(schemaAdditions) as Record<string, unknown>;
    if (!isPlainObject(overrides)) {
      return schema;
    }
    // Never let CMS-authored additions change the node's identity/type/context.
    const { merged, strippedReserved } = deepMergeSchemaNodes(schema, overrides);
    if (strippedReserved.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(`[SchemaAdditions] Ignored reserved key(s) @id/@type/@context for "${schema['@id']}".`);
    }
    return merged;
  } catch {
    // eslint-disable-next-line no-console
    console.warn(`[SchemaAdditions] Invalid JSON for product "${schema['@id']}": skipping merge.`);
    return schema;
  }
}
